/**
 * The upload queue behind the library page.
 *
 * Files are taken one at a time. Two 400 MB videos sent at once over a store's
 * upstream link finish later than the same two sent in order, and a queue that
 * makes no promises about ordering is harder to read while you wait.
 *
 * A file is checked before it is queued, which is why a rejection is a state of
 * a queue row rather than an exception: the person dropped six files, four are
 * fine, and the two that are not need to say what to change without stopping the
 * other four.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../lib/api.js';
import type { MediaScope } from '../../lib/media.js';
import { probeFile } from '../../lib/probe.js';
import type { UploadStage } from '../../lib/upload.js';
import { isCancellation, uploadMedia } from '../../lib/upload.js';

export type QueueState = 'checking' | 'rejected' | 'waiting' | 'working' | 'done' | 'failed' | 'cancelled';

export interface QueueItem {
  id: string;
  fileName: string;
  bytes: number;
  storeId: string | null;
  state: QueueState;
  stage: UploadStage;
  uploadedBytes: number;
  /** Set on `rejected` and `failed`; the first line is the headline. */
  title: string;
  message: string;
}

interface Job {
  id: string;
  file: File;
  scope: MediaScope;
  title: string;
}

let nextId = 0;

function makeId(): string {
  nextId += 1;
  return `upload-${nextId}`;
}

/** Drops the extension, which is noise once the file name is already on the row. */
function defaultTitle(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  return stem.trim().slice(0, 200);
}

export interface UploadQueue {
  items: QueueItem[];
  /** True while anything is still checking, waiting or transferring. */
  busy: boolean;
  add: (files: readonly File[], scope: MediaScope) => void;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  clearSettled: () => void;
}

const SETTLED: readonly QueueState[] = ['rejected', 'done', 'failed', 'cancelled'];

export function useUploadQueue(onUploaded: (storeId: string | null) => void): UploadQueue {
  const [items, setItems] = useState<QueueItem[]>([]);

  const pending = useRef<Job[]>([]);
  const running = useRef(false);
  const controllers = useRef(new Map<string, AbortController>());

  // The page re-creates its invalidation callback on every render; the runner
  // must always call the current one, never the one captured when it started.
  const uploaded = useRef(onUploaded);
  useEffect(() => {
    uploaded.current = onUploaded;
  }, [onUploaded]);

  const patch = useCallback((id: string, changes: Partial<QueueItem>): void => {
    setItems((previous) =>
      previous.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
  }, []);

  const runOne = useCallback(
    async (job: Job): Promise<void> => {
      patch(job.id, { state: 'checking' });

      const verdict = await probeFile(job.file);
      if (!verdict.ok) {
        patch(job.id, { state: 'rejected', title: verdict.title, message: verdict.message });
        return;
      }

      const controller = new AbortController();
      controllers.current.set(job.id, controller);
      patch(job.id, { state: 'working', stage: 'starting', uploadedBytes: 0 });

      try {
        await uploadMedia(
          {
            file: job.file,
            kind: verdict.kind,
            contentType: verdict.contentType,
            probe: verdict.probe,
            title: job.title,
            scope: job.scope,
          },
          {
            signal: controller.signal,
            onProgress: (progress) => {
              patch(job.id, { stage: progress.stage, uploadedBytes: progress.uploadedBytes });
            },
          },
        );
        patch(job.id, { state: 'done', stage: 'done', uploadedBytes: job.file.size });
        uploaded.current(job.scope.storeId);
      } catch (cause) {
        if (isCancellation(cause)) {
          patch(job.id, {
            state: 'cancelled',
            title: 'Cancelled',
            message: 'Nothing was added to the library. The partial upload is discarded by S3 on its own.',
          });
          return;
        }
        patch(job.id, {
          state: 'failed',
          title: 'This file did not finish uploading',
          message: errorMessage(cause),
        });
      } finally {
        controllers.current.delete(job.id);
      }
    },
    [patch],
  );

  const drain = useCallback(async (): Promise<void> => {
    if (running.current) return;
    running.current = true;
    try {
      for (;;) {
        const job = pending.current.shift();
        if (job === undefined) break;
        await runOne(job);
      }
    } finally {
      running.current = false;
    }
  }, [runOne]);

  const add = useCallback(
    (files: readonly File[], scope: MediaScope): void => {
      if (files.length === 0) return;

      const jobs = files.map((file) => ({
        id: makeId(),
        file,
        scope,
        title: defaultTitle(file.name),
      }));

      setItems((previous) => [
        ...previous,
        ...jobs.map<QueueItem>((job) => ({
          id: job.id,
          fileName: job.file.name,
          bytes: job.file.size,
          storeId: scope.storeId,
          state: 'waiting',
          stage: 'starting',
          uploadedBytes: 0,
          title: '',
          message: '',
        })),
      ]);

      pending.current.push(...jobs);
      void drain();
    },
    [drain],
  );

  const cancel = useCallback((id: string): void => {
    const controller = controllers.current.get(id);
    if (controller !== undefined) {
      controller.abort();
      return;
    }

    // Not started yet: pull it out of the queue rather than letting it begin and
    // then immediately abort.
    pending.current = pending.current.filter((job) => job.id !== id);
    setItems((previous) =>
      previous.map((item) =>
        item.id === id && (item.state === 'waiting' || item.state === 'checking')
          ? {
              ...item,
              state: 'cancelled',
              title: 'Cancelled',
              message: 'Removed from the queue before it started.',
            }
          : item,
      ),
    );
  }, []);

  const dismiss = useCallback((id: string): void => {
    setItems((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const clearSettled = useCallback((): void => {
    setItems((previous) => previous.filter((item) => !SETTLED.includes(item.state)));
  }, []);

  return {
    items,
    busy: items.some((item) => !SETTLED.includes(item.state)),
    add,
    cancel,
    dismiss,
    clearSettled,
  };
}
